import IntranetLayout from "@/components/IntranetLayout";
import { useState, useEffect, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  COURSE_MODULES, PASS_THRESHOLD, FINAL_PASS_THRESHOLD, MODULE_COUNT, TOTAL_QUESTIONS,
  FINAL_ASSESSMENT, FINAL_QUESTION_COUNT,
  type QuizQuestion, type FinalQuestion, type ModuleDocumentRef,
} from "@/data/onboardingCourse";
import {
  CheckCircle, BookOpen, HelpCircle, ChevronRight, ChevronLeft,
  Award, RotateCcw, Clock, Menu, X, Anchor, Shield, Ship,
  ShieldAlert, HeartPulse, Waves, Scale, Building2, Lock, Moon,
  AlertTriangle, FileText, Eye, Download, Timer,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useDocumentReadingTime, formatReadingTime } from "@/hooks/useDocumentReadingTime";
import velocityLogo from "@/assets/velocity-logo-light.png";

const ICON_MAP: Record<string, React.ReactNode> = {
  Anchor: <Anchor className="w-5 h-5" />,
  ShieldAlert: <ShieldAlert className="w-5 h-5" />,
  HeartPulse: <HeartPulse className="w-5 h-5" />,
  Waves: <Waves className="w-5 h-5" />,
  Scale: <Scale className="w-5 h-5" />,
  Building2: <Building2 className="w-5 h-5" />,
  Lock: <Lock className="w-5 h-5" />,
  Moon: <Moon className="w-5 h-5" />,
};

type Screen = "welcome" | "module" | "final" | "certificate";
type Tab = "reading" | "quiz";

const EmployeeOnboarding = () => {
  const { user, fullName } = useAuth();
  const queryClient = useQueryClient();
  const [screen, setScreen] = useState<Screen>("welcome");
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<Tab>("reading");
  const [completedModules, setCompletedModules] = useState<Record<number, number>>({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [moduleCorrect, setModuleCorrect] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Final assessment state
  const [finalAnswers, setFinalAnswers] = useState<Record<number, number>>({});
  const [finalCurrentQ, setFinalCurrentQ] = useState(0);
  const [finalSubmitted, setFinalSubmitted] = useState(false);
  const [finalShowResult, setFinalShowResult] = useState(false);
  const [finalSelectedAnswer, setFinalSelectedAnswer] = useState<number | null>(null);
  
  // Document viewer state
  const [viewingDocId, setViewingDocId] = useState<string | null>(null);
  const [viewingDocName, setViewingDocName] = useState("");
  const [viewingDocUrl, setViewingDocUrl] = useState<string | null>(null);

  // Track reading time for currently viewed document
  useDocumentReadingTime(viewingDocId, viewingDocName, "onboarding", !!viewingDocUrl);

  // Load operating documents for embedding in modules
  const { data: operatingDocs = [] } = useQuery({
    queryKey: ["onboarding-operating-docs"],
    queryFn: async () => {
      const { data } = await supabase
        .from("operating_documents")
        .select("*")
        .eq("is_archived", false)
        .order("name");
      return data ?? [];
    },
  });

  // Load reading time records for current user
  const { data: readingTimes = [] } = useQuery({
    queryKey: ["doc-reading-times", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data } = await supabase
        .from("document_reading_time" as any)
        .select("*")
        .eq("user_id", user.id);
      return (data as any) ?? [];
    },
    enabled: !!user,
  });

  const getReadingTime = (docId: string): number => {
    const record = readingTimes.find((r: any) => r.document_id === docId);
    return record ? (record as any).total_seconds : 0;
  };

  // Match operating documents to module's required document refs
  const getModuleDocuments = useCallback((refs?: ModuleDocumentRef[]) => {
    if (!refs || refs.length === 0) return [];
    return refs.map((ref) => {
      const matched = operatingDocs.filter((doc: any) => {
        const name = doc.name.toLowerCase();
        const desc = (doc.description || "").toLowerCase();
        const cat = (doc.category || "").toLowerCase();
        const matchesKeyword = ref.keywords.some(
          (kw) => name.includes(kw.toLowerCase()) || desc.includes(kw.toLowerCase())
        );
        const matchesCategory = !ref.category || cat === ref.category.toLowerCase();
        return matchesKeyword && matchesCategory;
      });
      return { ref, documents: matched };
    });
  }, [operatingDocs]);

  const handleViewDocument = async (doc: any) => {
    const { data } = await supabase.storage
      .from("operating-documents")
      .createSignedUrl(doc.file_path, 600);
    if (data?.signedUrl) {
      setViewingDocId(doc.id);
      setViewingDocName(doc.name);
      setViewingDocUrl(data.signedUrl);
    }
  };

  const handleCloseDocViewer = () => {
    setViewingDocUrl(null);
    setViewingDocId(null);
    setViewingDocName("");
  };

  // Load existing completion
  const { data: existingCompletion } = useQuery({
    queryKey: ["onboarding-completion", user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from("onboarding_completions")
        .select("*")
        .eq("user_id", user.id)
        .order("completed_at", { ascending: false })
        .limit(1);
      return data?.[0] || null;
    },
    enabled: !!user,
  });

  // Load saved progress
  const { data: savedProgress } = useQuery({
    queryKey: ["onboarding-progress", user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from("onboarding_progress" as any)
        .select("*")
        .eq("user_id", user.id)
        .limit(1);
      return (data as any)?.[0] || null;
    },
    enabled: !!user,
  });

  // Restore progress on load
  useEffect(() => {
    if (savedProgress && Object.keys(completedModules).length === 0 && screen === "welcome") {
      const saved = savedProgress as any;
      const modules = saved.completed_modules || {};
      if (Object.keys(modules).length > 0) {
        setCompletedModules(modules);
        setCurrentModuleIndex(saved.current_module_index || 0);
        setScreen("module");
      }
    }
  }, [savedProgress]);

  // Save progress mutation
  const saveProgressMutation = useMutation({
    mutationFn: async (data: { completed_modules: Record<number, number>; current_module_index: number }) => {
      if (!user) return;
      const payload = {
        user_id: user.id,
        completed_modules: data.completed_modules,
        current_module_index: data.current_module_index,
        updated_at: new Date().toISOString(),
      };
      // Upsert
      const { data: existing } = await supabase
        .from("onboarding_progress" as any)
        .select("id")
        .eq("user_id", user.id)
        .limit(1);
      if ((existing as any)?.length > 0) {
        await supabase.from("onboarding_progress" as any).update(payload as any).eq("user_id", user.id);
      } else {
        await supabase.from("onboarding_progress" as any).insert(payload as any);
      }
    },
  });

  const saveProgress = useCallback((modules: Record<number, number>, moduleIdx: number) => {
    saveProgressMutation.mutate({ completed_modules: modules, current_module_index: moduleIdx });
  }, [saveProgressMutation]);

  const completedCount = Object.keys(completedModules).length;
  const totalModuleCorrect = useMemo(() => Object.values(completedModules).reduce((a, b) => a + b, 0), [completedModules]);
  const moduleQuestionCount = useMemo(() => COURSE_MODULES.reduce((acc, m) => acc + m.questions.length, 0), []);
  const moduleScorePercent = completedCount > 0 ? Math.round((totalModuleCorrect / moduleQuestionCount) * 100) : 0;
  const allModulesComplete = completedCount === MODULE_COUNT;

  const currentModule = COURSE_MODULES[currentModuleIndex];

  const handleStartCourse = () => {
    setScreen("module");
    setCurrentModuleIndex(0);
    setActiveTab("reading");
  };

  const handleResumeCourse = () => {
    setScreen("module");
    setActiveTab("reading");
  };

  const handleSelectModule = (idx: number) => {
    setCurrentModuleIndex(idx);
    setActiveTab("reading");
    setScreen("module");
    setSidebarOpen(false);
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setModuleCorrect(0);
  };

  const handleSubmitAnswer = () => {
    if (selectedAnswer === null) return;
    setShowResult(true);
    const isCorrect = selectedAnswer === currentModule.questions[currentQuestion].correctIndex;
    if (isCorrect) setModuleCorrect((p) => p + 1);

    setTimeout(() => {
      if (currentQuestion < currentModule.questions.length - 1) {
        setCurrentQuestion((p) => p + 1);
        setSelectedAnswer(null);
        setShowResult(false);
      } else {
        const correct = isCorrect ? moduleCorrect + 1 : moduleCorrect;
        const newCompleted = { ...completedModules, [currentModule.id]: correct };
        setCompletedModules(newCompleted);
        setActiveTab("reading");
        setCurrentQuestion(0);
        setSelectedAnswer(null);
        setShowResult(false);
        setModuleCorrect(0);
        saveProgress(newCompleted, currentModuleIndex);

        if (Object.keys(newCompleted).length === MODULE_COUNT) {
          toast.success("All modules complete! Proceed to the Final Assessment.");
        } else {
          toast.success(`Module ${currentModule.id} complete! Score: ${correct}/${currentModule.questions.length}`);
        }
      }
    }, 1500);
  };

  // Final assessment
  const handleStartFinal = () => {
    setScreen("final");
    setFinalCurrentQ(0);
    setFinalAnswers({});
    setFinalSubmitted(false);
    setFinalShowResult(false);
    setFinalSelectedAnswer(null);
  };

  const handleFinalSubmitAnswer = () => {
    if (finalSelectedAnswer === null) return;
    setFinalShowResult(true);
    const newAnswers = { ...finalAnswers, [finalCurrentQ]: finalSelectedAnswer };
    setFinalAnswers(newAnswers);

    setTimeout(() => {
      if (finalCurrentQ < FINAL_QUESTION_COUNT - 1) {
        setFinalCurrentQ((p) => p + 1);
        setFinalSelectedAnswer(null);
        setFinalShowResult(false);
      } else {
        // Calculate final score
        const finalScore = FINAL_ASSESSMENT.reduce((acc, q, i) => acc + (newAnswers[i] === q.correctIndex ? 1 : 0), 0);
        const finalPct = Math.round((finalScore / FINAL_QUESTION_COUNT) * 100);
        const didPass = finalPct >= FINAL_PASS_THRESHOLD;
        setFinalSubmitted(true);
        saveFinalCompletion(finalScore, finalPct, didPass);
        setTimeout(() => setScreen("certificate"), 500);
      }
    }, 1500);
  };

  const saveFinalCompletion = async (total: number, pct: number, didPass: boolean) => {
    if (!user) return;
    try {
      await supabase.from("onboarding_completions").insert({
        user_id: user.id,
        score_percent: pct,
        total_correct: total,
        total_questions: FINAL_QUESTION_COUNT,
        passed: didPass,
      });
      queryClient.invalidateQueries({ queryKey: ["onboarding-completion", user?.id] });
    } catch (e) {
      console.error("Failed to save completion", e);
    }
  };

  const handleRestart = () => {
    setScreen("welcome");
    setCompletedModules({});
    setCurrentModuleIndex(0);
    setActiveTab("reading");
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setModuleCorrect(0);
    setFinalAnswers({});
    setFinalCurrentQ(0);
    setFinalSubmitted(false);
  };

  // ─── WELCOME SCREEN ───
  if (screen === "welcome") {
    const hasProgress = completedCount > 0;
    return (
      <IntranetLayout>
        <div className="max-w-2xl mx-auto py-8">
          {/* Brand header */}
          <div className="gradient-navy rounded-2xl p-8 mb-8 text-center relative overflow-hidden">
            <div className="grid-overlay absolute inset-0 pointer-events-none" />
            <div className="relative z-10">
              <img src={velocityLogo} alt="Velocity Offshore" className="h-12 mx-auto mb-4 opacity-90" />
              <h1 className="font-display text-2xl text-primary-foreground mb-2">
                Employee Onboarding Programme
              </h1>
              <p className="text-sm text-primary-foreground/60 font-body">
                8 Modules + Final Assessment — Velocity, Powered by Vigilance
              </p>
              <div className="flex items-center justify-center gap-6 mt-4">
                <div className="flex items-center gap-1.5 text-xs text-primary-foreground/50 font-body">
                  <Shield className="w-3.5 h-3.5 text-teal" /> ISM Compliant
                </div>
                <div className="flex items-center gap-1.5 text-xs text-primary-foreground/50 font-body">
                  <Ship className="w-3.5 h-3.5 text-teal" /> ABS Classed
                </div>
                <div className="flex items-center gap-1.5 text-xs text-primary-foreground/50 font-body">
                  <Anchor className="w-3.5 h-3.5 text-teal" /> RMI Flagged
                </div>
              </div>
            </div>
          </div>

          {existingCompletion && (
            <Card className="mb-6 border-accent/30 bg-accent/5">
              <CardContent className="p-4 text-center">
                <CheckCircle className="w-6 h-6 text-accent mx-auto mb-2" />
                <p className="text-sm font-body text-foreground">
                  You completed this course on {new Date(existingCompletion.completed_at).toLocaleDateString()} with a score of {existingCompletion.score_percent}% — {existingCompletion.passed ? "PASSED" : "NOT PASSED"}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Progress indicator if resuming */}
          {hasProgress && !existingCompletion && (
            <Card className="mb-6 border-secondary/30 bg-secondary/5">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-2">
                  <Clock className="w-5 h-5 text-secondary" />
                  <p className="text-sm font-display text-foreground">Course In Progress</p>
                </div>
                <Progress value={(completedCount / MODULE_COUNT) * 100} className="h-2 mb-2" />
                <p className="text-xs text-muted-foreground font-body">{completedCount}/{MODULE_COUNT} modules completed — your progress is saved automatically</p>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-3 gap-4 mb-8">
            {[
              { value: "8", label: "Modules" },
              { value: "~170", label: "Minutes" },
              { value: "80%", label: "Final Pass Mark" },
            ].map((stat) => (
              <Card key={stat.label} className="border-border/50">
                <CardContent className="p-4 text-center">
                  <p className="text-2xl font-display accent-text">{stat.value}</p>
                  <p className="text-xs text-muted-foreground font-body">{stat.label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="space-y-2 mb-8">
            {COURSE_MODULES.map((m) => {
              const done = completedModules[m.id] !== undefined;
              return (
                <Card key={m.id} className={cn("transition-colors group", done ? "border-accent/20 bg-accent/5" : "hover:border-secondary/30")}>
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center transition-colors", done ? "bg-accent/10 text-accent" : "bg-primary/5 text-muted-foreground group-hover:bg-secondary/10")}>
                      {ICON_MAP[m.icon] || <FileText className="w-5 h-5" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-display text-foreground">{m.title}</p>
                      <p className="text-xs text-muted-foreground font-body">{m.estTime} — {m.subtitle}</p>
                    </div>
                    {done ? (
                      <div className="flex items-center gap-1">
                        <CheckCircle className="w-4 h-4 text-accent" />
                        <span className="text-xs text-accent font-medium">{completedModules[m.id]}/{m.questions.length}</span>
                      </div>
                    ) : (
                      <ChevronRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-secondary transition-colors" />
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <p className="text-xs text-muted-foreground text-center mb-4 font-body">
            This induction is a prerequisite for EMGL contractor clearance. A 16-question Final Assessment (80% pass mark) follows module completion. Your completion record will be shared with the HSEQ Manager.
          </p>

          {hasProgress && !existingCompletion ? (
            <Button onClick={handleResumeCourse} className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-display text-base py-6 glow-teal">
              Resume Course <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleStartCourse} className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-display text-base py-6 glow-teal">
              {existingCompletion ? "Retake Course" : "Begin Course"} <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          )}
        </div>
      </IntranetLayout>
    );
  }

  // ─── CERTIFICATE SCREEN ───
  if (screen === "certificate") {
    const finalScore = FINAL_ASSESSMENT.reduce((acc, q, i) => acc + (finalAnswers[i] === q.correctIndex ? 1 : 0), 0);
    const finalPct = Math.round((finalScore / FINAL_QUESTION_COUNT) * 100);
    const finalPassed = finalPct >= FINAL_PASS_THRESHOLD;

    return (
      <IntranetLayout>
        <div className="max-w-xl mx-auto py-8">
          <Card className="border-2 border-secondary/30 overflow-hidden shadow-xl">
            <div className="gradient-navy p-8 text-center relative overflow-hidden">
              <div className="grid-overlay absolute inset-0 pointer-events-none opacity-50" />
              <div className="relative z-10">
                <img src={velocityLogo} alt="Velocity Offshore" className="h-10 mx-auto mb-3 opacity-80" />
                <p className="text-xs text-primary-foreground/40 font-body tracking-[0.3em] uppercase mb-3">
                  Velocity Offshore Holdings LLC
                </p>
                <h1 className="text-primary-foreground font-display text-2xl">
                  Employee Onboarding Certificate
                </h1>
                <div className="w-16 h-[2px] mx-auto mt-4 bg-gradient-to-r from-secondary to-accent" />
              </div>
            </div>
            <CardContent className="p-8 text-center space-y-6">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-body mb-1">Awarded to</p>
                <p className="text-2xl font-display text-foreground">{fullName || "Employee"}</p>
              </div>

              <Badge className={cn(
                "text-lg px-6 py-2 font-display",
                finalPassed
                  ? "bg-accent hover:bg-accent text-accent-foreground"
                  : "bg-destructive hover:bg-destructive text-destructive-foreground"
              )}>
                {finalPassed ? "PASS" : "FAIL"}
              </Badge>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-3xl font-display accent-text">{finalPct}%</p>
                  <p className="text-xs text-muted-foreground font-body">Final Assessment Score</p>
                </div>
                <div>
                  <p className="text-3xl font-display accent-text">{finalScore}/{FINAL_QUESTION_COUNT}</p>
                  <p className="text-xs text-muted-foreground font-body">Questions Correct</p>
                </div>
              </div>

              <p className="text-sm text-muted-foreground font-body">
                Completed on {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
              </p>

              <div className="pt-4 border-t border-border">
                <p className="text-[10px] text-muted-foreground/60 font-body italic">
                  This certificate has been recorded in your personnel file. Velocity, Powered by Vigilance.
                </p>
              </div>

              <Button onClick={handleRestart} variant="outline" className="gap-2 border-secondary/30 text-secondary hover:bg-secondary/5">
                <RotateCcw className="w-4 h-4" /> Restart Course
              </Button>
            </CardContent>
          </Card>
        </div>
      </IntranetLayout>
    );
  }

  // ─── FINAL ASSESSMENT SCREEN ───
  if (screen === "final") {
    const fq = FINAL_ASSESSMENT[finalCurrentQ];
    return (
      <IntranetLayout>
        <div className="max-w-2xl mx-auto py-8">
          <div className="gradient-navy rounded-xl p-4 mb-6 relative overflow-hidden">
            <div className="grid-overlay absolute inset-0 pointer-events-none opacity-30" />
            <div className="relative z-10 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-foreground/10 flex items-center justify-center">
                <Award className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <h1 className="font-display text-lg text-primary-foreground">Final Assessment</h1>
                <div className="flex items-center gap-2 text-xs text-primary-foreground/50 font-body">
                  <span>{FINAL_QUESTION_COUNT} questions</span>
                  <span className="text-primary-foreground/20">|</span>
                  <span>{FINAL_PASS_THRESHOLD}% pass mark</span>
                  <span className="text-primary-foreground/20">|</span>
                  <span>2 questions per module</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-body text-muted-foreground">
              Question {finalCurrentQ + 1} of {FINAL_QUESTION_COUNT}
              <span className="ml-2 text-xs text-muted-foreground/60">(Module {fq.module}: {COURSE_MODULES[fq.module - 1].title})</span>
            </p>
            <Progress value={((finalCurrentQ + 1) / FINAL_QUESTION_COUNT) * 100} className="w-32 h-2" />
          </div>

          <Card className="border-border/50">
            <CardContent className="p-6">
              <h3 className="font-display text-base text-foreground mb-6">{fq.question}</h3>
              <div className="space-y-3">
                {fq.options.map((opt, oi) => {
                  let optClass = "border-border hover:border-secondary/40 cursor-pointer";
                  if (finalShowResult) {
                    if (oi === fq.correctIndex) optClass = "border-accent bg-accent/10";
                    else if (finalSelectedAnswer === oi && oi !== fq.correctIndex) optClass = "border-destructive bg-destructive/10";
                    else optClass = "border-border opacity-50";
                  } else if (finalSelectedAnswer === oi) {
                    optClass = "border-secondary bg-secondary/10";
                  }
                  return (
                    <button
                      key={oi}
                      disabled={finalShowResult}
                      onClick={() => setFinalSelectedAnswer(oi)}
                      className={cn("w-full text-left p-4 rounded-lg border-2 transition-all text-sm font-body", optClass)}
                    >
                      <span className="font-medium mr-2">{String.fromCharCode(65 + oi)}.</span>
                      {opt}
                      {finalShowResult && oi === fq.correctIndex && <CheckCircle className="w-4 h-4 text-accent inline ml-2" />}
                      {finalShowResult && finalSelectedAnswer === oi && oi !== fq.correctIndex && <X className="w-4 h-4 text-destructive inline ml-2" />}
                    </button>
                  );
                })}
              </div>

              {!finalShowResult && (
                <Button
                  onClick={handleFinalSubmitAnswer}
                  disabled={finalSelectedAnswer === null}
                  className="w-full mt-6 bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                >
                  Submit Answer
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </IntranetLayout>
    );
  }

  // ─── MODULE SCREEN ───
  const isModuleComplete = completedModules[currentModule.id] !== undefined;
  const q = currentModule.questions[currentQuestion];

  return (
    <IntranetLayout>
      <div className="flex gap-6 -mt-2">
        {/* Mobile sidebar toggle */}
        <Button
          variant="ghost" size="icon"
          className="lg:hidden fixed top-20 left-4 z-50 bg-background shadow-md"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>

        {/* Sidebar */}
        <div className={cn(
          "w-64 flex-shrink-0 space-y-3 transition-all",
          "fixed lg:relative lg:block z-40 bg-background lg:bg-transparent p-4 lg:p-0 top-16 left-0 bottom-0 border-r lg:border-0",
          sidebarOpen ? "block" : "hidden lg:block"
        )}>
          <div className="gradient-navy rounded-xl p-3 mb-3 relative overflow-hidden">
            <div className="grid-overlay absolute inset-0 pointer-events-none opacity-30" />
            <div className="relative z-10 flex items-center gap-2">
              <img src={velocityLogo} alt="Velocity Offshore" className="h-5 opacity-70" />
              <span className="text-[10px] text-primary-foreground/40 font-body uppercase tracking-wider">Onboarding</span>
            </div>
          </div>

          <div className="mb-4">
            <p className="text-xs text-muted-foreground font-body uppercase tracking-wider mb-2">Course Progress</p>
            <Progress value={(completedCount / MODULE_COUNT) * 100} className="h-2" />
            <p className="text-xs text-muted-foreground font-body mt-1">{completedCount}/{MODULE_COUNT} modules</p>
          </div>

          {COURSE_MODULES.map((m, i) => {
            const done = completedModules[m.id] !== undefined;
            const active = i === currentModuleIndex;
            return (
              <button
                key={m.id}
                onClick={() => handleSelectModule(i)}
                className={cn(
                  "w-full text-left p-3 rounded-lg flex items-center gap-3 transition-all text-sm font-body",
                  active ? "bg-secondary/10 border border-secondary/20" : "hover:bg-muted/50",
                  done && !active && "opacity-70"
                )}
              >
                <div className={cn("w-7 h-7 rounded flex items-center justify-center", done ? "text-accent" : active ? "text-secondary" : "text-muted-foreground")}>
                  {ICON_MAP[m.icon] || <FileText className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={cn("truncate text-xs", active ? "text-secondary font-medium" : "text-foreground")}>{m.title}</p>
                  <p className="text-[10px] text-muted-foreground">{m.estTime}</p>
                </div>
                {done && (
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5 text-accent" />
                    <span className="text-[10px] text-accent font-medium">{completedModules[m.id]}/{m.questions.length}</span>
                  </div>
                )}
              </button>
            );
          })}

          {/* Final assessment link */}
          {allModulesComplete && (
            <button
              onClick={handleStartFinal}
              className="w-full text-left p-3 rounded-lg flex items-center gap-3 transition-all text-sm font-body bg-secondary/10 border border-secondary/20 mt-2"
            >
              <div className="w-7 h-7 rounded flex items-center justify-center text-secondary">
                <Award className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="truncate text-xs text-secondary font-medium">Final Assessment</p>
                <p className="text-[10px] text-muted-foreground">{FINAL_QUESTION_COUNT} questions</p>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-secondary" />
            </button>
          )}

          {completedCount > 0 && (
            <div className="pt-3 border-t border-border">
              <p className="text-xs text-muted-foreground font-body">Module Score</p>
              <p className={cn("text-lg font-display", moduleScorePercent >= PASS_THRESHOLD ? "text-accent" : "text-destructive")}>
                {moduleScorePercent}%
              </p>
              <p className="text-[10px] text-muted-foreground font-body mt-0.5">Progress saved automatically</p>
            </div>
          )}
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Module header */}
          <div className="gradient-navy rounded-xl p-4 mb-6 relative overflow-hidden">
            <div className="grid-overlay absolute inset-0 pointer-events-none opacity-30" />
            <div className="relative z-10 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-foreground/10 flex items-center justify-center text-primary-foreground">
                {ICON_MAP[currentModule.icon] || <FileText className="w-5 h-5" />}
              </div>
              <div>
                <h1 className="font-display text-lg text-primary-foreground">Module {currentModule.id} — {currentModule.title}</h1>
                <div className="flex items-center gap-2 text-xs text-primary-foreground/50 font-body">
                  <Clock className="w-3 h-3" /> {currentModule.estTime}
                  <span className="text-primary-foreground/20">|</span>
                  <span>{currentModule.subtitle}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-6">
            <Button
              variant={activeTab === "reading" ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveTab("reading")}
              className={cn("gap-2", activeTab === "reading" && "bg-secondary hover:bg-secondary/90 text-secondary-foreground")}
            >
              <BookOpen className="w-4 h-4" /> Reading
            </Button>
            <Button
              variant={activeTab === "quiz" ? "default" : "outline"}
              size="sm"
              onClick={() => !isModuleComplete && setActiveTab("quiz")}
              disabled={isModuleComplete}
              className={cn("gap-2", activeTab === "quiz" && "bg-secondary hover:bg-secondary/90 text-secondary-foreground")}
            >
              <HelpCircle className="w-4 h-4" /> Knowledge Check
              {isModuleComplete && <CheckCircle className="w-3 h-3 text-accent ml-1" />}
            </Button>
          </div>

          {/* Reading tab */}
          {activeTab === "reading" && (
            <div className="space-y-6">
              {currentModule.sections.map((section, i) => (
                <Card key={i} className="border-border/50 hover:border-secondary/20 transition-colors">
                  <CardContent className="p-6">
                    <h2 className="font-display text-base text-foreground mb-3 flex items-center gap-2">
                      <div className="w-1 h-5 rounded-full bg-gradient-to-b from-secondary to-accent" />
                      {section.title}
                    </h2>
                    <div className="text-sm text-muted-foreground font-body leading-relaxed whitespace-pre-line mb-4">
                      {section.content}
                    </div>
                    {section.points && section.points.length > 0 && (
                      <ul className="space-y-2 mt-3">
                        {section.points.map((point, pi) => (
                          <li key={pi} className="flex items-start gap-2 text-sm font-body text-foreground/80">
                            <ChevronRight className="w-3.5 h-3.5 text-secondary mt-0.5 flex-shrink-0" />
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </CardContent>
                </Card>
              ))}

              {/* Required Documents Section */}
              {(() => {
                const moduleDocRefs = getModuleDocuments(currentModule.requiredDocuments);
                const hasAnyDocs = moduleDocRefs.some(mr => mr.documents.length > 0);
                if (!hasAnyDocs && (!currentModule.requiredDocuments || currentModule.requiredDocuments.length === 0)) return null;
                return (
                  <Card className="border-secondary/20 bg-secondary/5">
                    <CardContent className="p-6">
                      <h2 className="font-display text-base text-foreground mb-1 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-secondary" />
                        Required Reading — Associated Documents
                      </h2>
                      <p className="text-xs text-muted-foreground font-body mb-4">
                        Review the documents below as part of this module. Your reading time is tracked automatically.
                      </p>
                      <div className="space-y-3">
                        {moduleDocRefs.map((mr, idx) => (
                          <div key={idx}>
                            <p className="text-xs font-display text-foreground/70 mb-2">{mr.ref.label}</p>
                            {mr.documents.length > 0 ? (
                              <div className="space-y-2">
                                {mr.documents.map((doc: any) => {
                                  const readTime = getReadingTime(doc.id);
                                  return (
                                    <div
                                      key={doc.id}
                                      className="flex items-center gap-3 p-3 rounded-lg bg-background border border-border/50 hover:border-secondary/30 transition-colors"
                                    >
                                      <div className="w-8 h-8 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
                                        <FileText className="w-4 h-4 text-secondary" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-body text-foreground truncate">{doc.name}</p>
                                        <div className="flex items-center gap-3 mt-0.5">
                                          {doc.version && (
                                            <span className="text-[10px] text-muted-foreground">v{doc.version}</span>
                                          )}
                                          {readTime > 0 && (
                                            <span className="flex items-center gap-1 text-[10px] text-accent">
                                              <Timer className="w-3 h-3" /> {formatReadingTime(readTime)}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="gap-1.5 border-secondary/30 text-secondary hover:bg-secondary/10 text-xs"
                                        onClick={() => handleViewDocument(doc)}
                                      >
                                        <Eye className="w-3.5 h-3.5" /> Read
                                      </Button>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <p className="text-xs text-muted-foreground/60 font-body italic pl-2">
                                No matching document found — check Documents &amp; SOPs
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Document Viewer Modal */}
              {viewingDocUrl && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
                  <div className="bg-background rounded-xl border border-border shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden">
                    <div className="flex items-center justify-between p-4 border-b border-border">
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText className="w-5 h-5 text-secondary flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-display text-foreground truncate">{viewingDocName}</p>
                          <p className="text-[10px] text-muted-foreground font-body flex items-center gap-1.5">
                            <Timer className="w-3 h-3 text-accent" /> Reading time is being tracked
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleCloseDocViewer}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <X className="w-5 h-5" />
                      </Button>
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <iframe
                        src={viewingDocUrl}
                        className="w-full h-full border-0"
                        title={viewingDocName}
                      />
                    </div>
                  </div>
                </div>
              )}

              {!isModuleComplete && (
                <Button
                  onClick={() => setActiveTab("quiz")}
                  className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-display py-5"
                >
                  Start Knowledge Check <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              )}

              {isModuleComplete && currentModuleIndex < MODULE_COUNT - 1 && (
                <Button
                  onClick={() => handleSelectModule(currentModuleIndex + 1)}
                  className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-display py-5"
                >
                  Next Module <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              )}

              {isModuleComplete && allModulesComplete && (
                <Button
                  onClick={handleStartFinal}
                  className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-display py-5 gap-2"
                >
                  <Award className="w-5 h-5" /> Begin Final Assessment
                </Button>
              )}
            </div>
          )}

          {/* Quiz tab */}
          {activeTab === "quiz" && !isModuleComplete && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-body text-muted-foreground">
                  Question {currentQuestion + 1} of {currentModule.questions.length}
                </p>
                <Progress value={((currentQuestion + 1) / currentModule.questions.length) * 100} className="w-32 h-2" />
              </div>

              <Card className="border-border/50">
                <CardContent className="p-6">
                  <h3 className="font-display text-base text-foreground mb-6">{q.question}</h3>

                  <div className="space-y-3">
                    {q.options.map((opt, oi) => {
                      const isCorrect = oi === q.correctIndex;
                      const isSelected = selectedAnswer === oi;
                      let optClass = "border-border hover:border-secondary/40 cursor-pointer";

                      if (showResult) {
                        if (isCorrect) optClass = "border-accent bg-accent/10";
                        else if (isSelected && !isCorrect) optClass = "border-destructive bg-destructive/10";
                        else optClass = "border-border opacity-50";
                      } else if (isSelected) {
                        optClass = "border-secondary bg-secondary/10";
                      }

                      return (
                        <button
                          key={oi}
                          disabled={showResult}
                          onClick={() => setSelectedAnswer(oi)}
                          className={cn(
                            "w-full text-left p-4 rounded-lg border-2 transition-all text-sm font-body",
                            optClass
                          )}
                        >
                          <span className="font-medium mr-2">{String.fromCharCode(65 + oi)}.</span>
                          {opt}
                          {showResult && isCorrect && <CheckCircle className="w-4 h-4 text-accent inline ml-2" />}
                          {showResult && isSelected && !isCorrect && <X className="w-4 h-4 text-destructive inline ml-2" />}
                        </button>
                      );
                    })}
                  </div>

                  {showResult && (
                    <div className={cn(
                      "mt-4 p-4 rounded-lg text-sm font-body",
                      selectedAnswer === q.correctIndex ? "bg-accent/10 text-accent-foreground" : "bg-destructive/10 text-destructive"
                    )}>
                      {q.explanation}
                    </div>
                  )}

                  {!showResult && (
                    <Button
                      onClick={handleSubmitAnswer}
                      disabled={selectedAnswer === null}
                      className="w-full mt-6 bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                    >
                      Submit Answer
                    </Button>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </IntranetLayout>
  );
};

export default EmployeeOnboarding;
